import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import Autoplay from 'embla-carousel-autoplay';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Home, 
  Shield, 
  Camera,
  Clock, 
  Sparkles,
  Phone,
  Globe,
  ArrowRight,
  CheckCircle,
  Check,
  Star,
  Quote,
  Droplets,
  Wind,
  AlertTriangle,
  CloudRain,
  Users,
  Eye,
  Wrench,
  Calendar,
  Key,
  FileCheck,
  ClipboardCheck,
  Lock,
  UserCheck,
  ChevronRight,
  Mail,
  MapPin,
  Zap,
  Building,
  HeartHandshake,
  Heart,
  Menu,
  X
} from 'lucide-react';
import { getFullImageUrl } from '../lib/imageUtils';
import FranceMap from '../components/FranceMap';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const LandingPage = () => {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'fr');
  const [profileContent, setProfileContent] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    frequency: '',
    services: [],
    message: ''
  });
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const prestationsCol1 = [
    { id: 'menage', fr: 'Ménage', en: 'Cleaning' },
    { id: 'inspection', fr: 'Inspection générale', en: 'General inspection' },
    { id: 'gestion_location', fr: 'Gestion complète de la location', en: 'Full rental management' },
    { id: 'entree_sortie', fr: 'Entrée / Sortie locataires', en: 'Tenant check-in / check-out' },
    { id: 'preparation', fr: 'Préparation avant votre arrivée', en: 'Pre-arrival preparation' },
    { id: 'courses', fr: 'Courses de nécessité', en: 'Essential shopping' },
    { id: 'alarme', fr: 'Problème technique / Alarme', en: 'Technical issue / Alarm' },
  ];
  const prestationsCol2 = [
    { id: 'peinture', fr: 'Travaux de peinture', en: 'Painting work' },
    { id: 'piscine', fr: 'Mise en service piscine', en: 'Pool commissioning' },
    { id: 'plantations', fr: 'Plantations extérieures', en: 'Outdoor planting' },
    { id: 'entretien_ext', fr: 'Entretien extérieur', en: 'Exterior maintenance' },
    { id: 'visites', fr: 'Visites régulières', en: 'Regular visits' },
    { id: 'autres', fr: 'Autres', en: 'Other' },
    { id: 'informatique', fr: 'Problème informatique / réseau', en: 'IT / Network issue' },
  ];

  const toggleService = (id) => {
    setQuoteForm(prev => ({
      ...prev,
      services: prev.services.includes(id)
        ? prev.services.filter(s => s !== id)
        : [...prev.services, id]
    }));
  };

  useEffect(() => {
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [profileRes, reviewsRes] = await Promise.all([
        axios.get(`${API_URL}/content/profile`),
        axios.get(`${API_URL}/reviews`).catch(() => ({ data: [] }))
      ]);
      setProfileContent(profileRes.data.content);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error fetching content:', error);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    // Validate all required fields
    if (!quoteForm.name || !quoteForm.email || !quoteForm.phone || !quoteForm.propertyType || !quoteForm.frequency || !quoteForm.message) {
      toast.error(language === 'fr' ? 'Veuillez remplir tous les champs obligatoires.' : 'Please fill in all required fields.');
      return;
    }
    
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API_URL}/quote-request`, {
        name: quoteForm.name,
        email: quoteForm.email,
        phone: quoteForm.phone,
        property_type: quoteForm.propertyType,
        frequency: quoteForm.frequency,
        services: quoteForm.services,
        message: quoteForm.message
      });
      
      if (response.data.success) {
        toast.success(language === 'fr' ? 'Demande envoyée avec succès! Nous vous contacterons bientôt.' : 'Quote request sent successfully! We will contact you soon.');
        setQuoteForm({ name: '', email: '', phone: '', propertyType: '', frequency: '', message: '' });
      }
    } catch (error) {
      console.error('Quote submission error:', error);
      toast.error(language === 'fr' ? 'Erreur lors de l\'envoi. Veuillez réessayer.' : 'Error sending request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const t = (key) => {
    const translations = {
      en: {
        // Header
        login: "Login",
        register: "Register",
        
        // Hero
        heroTitle: "Your home protected, maintained, and ready while you're away.",
        heroSub: "Professional concierge services for property owners away from home",
        requestQuote: "Request a Quote",
        seePlans: "See Plans & Pricing",
        viewAllPlans: "Register to See All Plans",
        trustBullet1: "Photo report after every visit",
        trustBullet2: "Emergency response coordination",
        trustBullet3: "Discreet & vetted contractor network",
        hourlyPrice: "From 20€/hour",
        
        // Pain Points
        painTitle: "What Property Owners Worry About",
        painSub: "We detect early, act fast, and keep you informed.",
        pain1: "Water leaks & burst pipes",
        pain2: "Humidity / mold",
        pain3: "Break-ins & suspicious activity",
        pain4: "Storm damage",
        pain5: "Contractor unreliability",
        
        // 4 Pillars
        pillarsTitle: "Our Services",
        pillarsSub: "Comprehensive property care tailored to your needs",
        pillar1Title: "Property Monitoring & Security",
        pillar1Bullets: ["Regular scheduled inspections", "Photo documentation", "Security checks", "Key holder service", "Emergency alerts"],
        pillar2Title: "Maintenance & Contractor Management",
        pillar2Bullets: ["Vetted contractor network", "Quote comparison", "Work supervision", "Quality verification", "Invoice management"],
        pillar3Title: "Arrival Preparation",
        pillar3Bullets: ["Deep cleaning", "Heating/AC pre-start", "Fresh provisions", "Pool preparation", "Garden tidying"],
        pillar4Title: "Premium Lifestyle Concierge",
        pillar4Bullets: ["Restaurant reservations", "Event tickets", "Transport arrangements", "Personal shopping", "Custom requests"],
        learnMore: "Learn more",
        
        // Plans
        plansTitle: "Simple, Transparent Pricing",
        plansSub: "Choose the level of care that suits your property",
        basicPlan: "Pack Essential",
        comfortPlan: "Pack Comfort",
        premiumPlan: "Pack Premium",
        startingFrom: "Starting from",
        perMonth: "/month",
        viewAllPlans: "View All Plans",
        
        // How it Works
        howTitle: "How It Works",
        howSub: "A simple, transparent process",
        step1Title: "Onboarding",
        step1Desc: "Keys handover + property checklist",
        step2Title: "Regular Visits",
        step2Desc: "Scheduled inspections + photo reports",
        step3Title: "Issue Detection",
        step3Desc: "Problems identified → your approval",
        step4Title: "Contractor Management",
        step4Desc: "Work supervised → completion proof",
        step5Title: "Arrival Ready",
        step5Desc: "Property prepared on demand",
        
        // Trust
        trustTitle: "Our Standards",
        trustSub: "Professional, documented, and discreet",
        trust1: "Standardized inspection checklist",
        trust2: "Time-stamped photo reporting",
        trust3: "Insurance-friendly documentation",
        trust4: "Confidentiality & discretion",
        trust5: "Preferred partners network",
        
        // Final CTA
        ctaTitle: "Get Your Personalized Quote",
        ctaSub: "Tell us about your property and how often you're away",
        yourName: "Your Name",
        yourEmail: "Email",
        yourPhone: "Phone",
        propertyType: "Property Type",
        house: "House",
        apartment: "Apartment",
        villa: "Villa",
        frequency: "How often are you away?",
        freqOccasional: "A few weeks per year",
        freqRegular: "Several months per year",
        freqMost: "Most of the year",
        message: "Additional details",
        submitQuote: "Request Quote",
        
        // Footer
        urgentCall: "Urgent Line",
        allRights: "All rights reserved."
      },
      fr: {
        // Header
        login: "Connexion",
        register: "Inscription",
        
        // Hero
        heroTitle: "Votre maison protégée, entretenue et prête pendant votre absence.",
        heroSub: "Services de conciergerie professionnels pour propriétaires absents",
        requestQuote: "Demander un Devis",
        seePlans: "Voir les Forfaits",
        viewAllPlans: "Inscrivez-vous pour voir tous les forfaits",
        trustBullet1: "Rapport photo après chaque visite",
        trustBullet2: "Coordination des urgences",
        trustBullet3: "Réseau d'artisans vérifiés et discrets",
        hourlyPrice: "À partir de 20€/heure",
        
        // Pain Points
        painTitle: "Ce qui Inquiète les Propriétaires",
        painSub: "Nous détectons tôt, agissons vite, et vous tenons informé.",
        pain1: "Fuites d'eau & canalisations",
        pain2: "Humidité / moisissures",
        pain3: "Cambriolages & activités suspectes",
        pain4: "Dégâts de tempête",
        pain5: "Artisans peu fiables",
        
        // 4 Pillars
        pillarsTitle: "Nos Services",
        pillarsSub: "Une prise en charge complète adaptée à vos besoins",
        pillar1Title: "Surveillance & Sécurité",
        pillar1Bullets: ["Inspections régulières programmées", "Documentation photo", "Vérifications sécurité", "Service gardien de clés", "Alertes urgentes"],
        pillar2Title: "Entretien & Gestion Artisans",
        pillar2Bullets: ["Réseau d'artisans vérifiés", "Comparaison de devis", "Supervision des travaux", "Vérification qualité", "Gestion des factures"],
        pillar3Title: "Préparation à l'Arrivée",
        pillar3Bullets: ["Nettoyage en profondeur", "Mise en route chauffage/clim", "Provisions fraîches", "Préparation piscine", "Entretien jardin"],
        pillar4Title: "Conciergerie Lifestyle Premium",
        pillar4Bullets: ["Réservations restaurants", "Billets événements", "Organisation transport", "Shopping personnalisé", "Demandes sur mesure"],
        learnMore: "En savoir plus",
        
        // Plans
        plansTitle: "Tarifs Simples et Transparents",
        plansSub: "Choisissez le niveau de service adapté à votre propriété",
        basicPlan: "Pack Essentiel",
        comfortPlan: "Pack Confort",
        premiumPlan: "Pack Premium",
        startingFrom: "À partir de",
        perMonth: "/mois",
        viewAllPlans: "Inscrivez-vous pour voir tous les forfaits",
        
        // How it Works
        howTitle: "Comment Ça Marche",
        howSub: "Un processus simple et transparent",
        step1Title: "Prise en charge",
        step1Desc: "Remise des clés + checklist propriété",
        step2Title: "Visites Régulières",
        step2Desc: "Inspections programmées + rapports photo",
        step3Title: "Détection Problèmes",
        step3Desc: "Anomalies identifiées → votre validation",
        step4Title: "Gestion Artisans",
        step4Desc: "Travaux supervisés → preuves achèvement",
        step5Title: "Prêt à l'Arrivée",
        step5Desc: "Propriété préparée sur demande",
        
        // Trust
        trustTitle: "Nos Standards",
        trustSub: "Professionnel, documenté et discret",
        trust1: "Checklist d'inspection standardisée",
        trust2: "Rapports photo horodatés",
        trust3: "Documentation compatible assurance",
        trust4: "Confidentialité & discrétion",
        trust5: "Réseau de partenaires privilégiés",
        
        // Final CTA
        ctaTitle: "Obtenez Votre Devis Personnalisé",
        ctaSub: "Dites-nous en plus sur votre propriété et vos absences",
        yourName: "Votre Nom",
        yourEmail: "Email",
        yourPhone: "Téléphone",
        propertyType: "Type de Propriété",
        house: "Maison",
        apartment: "Appartement",
        villa: "Villa",
        frequency: "Fréquence de vos absences?",
        freqOccasional: "Quelques semaines par an",
        freqRegular: "Plusieurs mois par an",
        freqMost: "La majeure partie de l'année",
        message: "Détails supplémentaires",
        submitQuote: "Demander un Devis",
        
        // Footer
        urgentCall: "Ligne Urgente",
        allRights: "Tous droits réservés."
      }
    };
    return translations[language]?.[key] || key;
  };

  const painPoints = [
    { image: 'https://images.pexels.com/photos/15206136/pexels-photo-15206136.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', key: 'pain1' },
    { image: 'https://images.pexels.com/photos/18351809/pexels-photo-18351809.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', key: 'pain2' },
    { image: 'https://images.pexels.com/photos/792034/pexels-photo-792034.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', key: 'pain3' },
    { image: 'https://images.pexels.com/photos/36237047/pexels-photo-36237047.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', key: 'pain4' },
    { image: 'https://images.pexels.com/photos/15798782/pexels-photo-15798782.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940', key: 'pain5' },
  ];

  const pillars = [
    { 
      icon: Eye, 
      titleKey: 'pillar1Title', 
      bulletsKey: 'pillar1Bullets',
      gradient: 'from-blue-500 to-blue-600',
      contactService: 'security'
    },
    { 
      icon: Wrench, 
      titleKey: 'pillar2Title', 
      bulletsKey: 'pillar2Bullets',
      gradient: 'from-emerald-500 to-emerald-600',
      contactService: 'maintenance'
    },
    { 
      icon: Sparkles, 
      titleKey: 'pillar3Title', 
      bulletsKey: 'pillar3Bullets',
      gradient: 'from-amber-500 to-amber-600',
      contactService: 'arrival'
    },
    { 
      icon: Star, 
      titleKey: 'pillar4Title', 
      bulletsKey: 'pillar4Bullets',
      gradient: 'from-purple-500 to-purple-600',
      contactService: 'lifestyle'
    },
  ];

  const steps = [
    { image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=300&q=80', titleKey: 'step1Title', descKey: 'step1Desc' },
    { image: 'https://images.unsplash.com/photo-1625384827802-1118262acfa6?auto=format&fit=crop&w=300&q=80', titleKey: 'step2Title', descKey: 'step2Desc' },
    { image: 'https://images.unsplash.com/flagged/photo-1564767609213-c75ee685263a?auto=format&fit=crop&w=300&q=80', titleKey: 'step3Title', descKey: 'step3Desc' },
    { image: 'https://images.unsplash.com/photo-1622936082250-b0cdf0073961?auto=format&fit=crop&w=300&q=80', titleKey: 'step4Title', descKey: 'step4Desc' },
    { image: 'https://images.unsplash.com/photo-1689966779282-3cd8fd1029c5?auto=format&fit=crop&w=300&q=80', titleKey: 'step5Title', descKey: 'step5Desc' },
  ];

  const trustItems = [
    { icon: ClipboardCheck, key: 'trust1' },
    { icon: Camera, key: 'trust2' },
    { icon: FileCheck, key: 'trust3' },
    { icon: Lock, key: 'trust4' },
    { icon: UserCheck, key: 'trust5' },
  ];

  const [selectedPlanIdx, setSelectedPlanIdx] = useState(null);

  const plans = [
    { name: 'basicPlan', price: 89, features: 3, included: [
      { fr: 'Ménage 3h', en: 'Cleaning 3h' },
      { fr: 'Entretien extérieur 1h', en: 'Exterior maintenance 1h' },
      { fr: '1 Inspection', en: '1 Inspection' },
    ]},
    { name: 'comfortPlan', price: 149, features: 5, popular: true, included: [
      { fr: 'Ménage 4h', en: 'Cleaning 4h' },
      { fr: 'Entretien extérieur 4h', en: 'Exterior maintenance 4h' },
      { fr: '2 Inspections', en: '2 Inspections' },
    ]},
    { name: 'premiumPlan', price: 249, features: 8, included: [
      { fr: 'Ménage 8h', en: 'Cleaning 8h' },
      { fr: 'Entretien extérieur 8h', en: 'Exterior maintenance 8h' },
      { fr: '4 Inspections', en: '4 Inspections' },
    ]},
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2 cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
              <img src="/logo.png" alt="3+ Conciergerie" className="h-10 sm:h-12 w-auto object-contain" />
              <span className="font-heading font-bold text-lg sm:text-xl text-foreground">Conciergerie</span>
            </Link>
            
            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-4">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="landing-language-btn">
                    <Globe className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => { setLanguage('en'); localStorage.setItem('language', 'en'); }}>
                    English
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setLanguage('fr'); localStorage.setItem('language', 'fr'); }}>
                    Français
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              
              <Link to="/login">
                <Button variant="ghost" data-testid="landing-login-btn">{t('login')}</Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary" data-testid="landing-register-btn">{t('register')}</Button>
              </Link>
              <Link to="/contact">
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="landing-contact-btn">
                  {language === 'fr' ? 'Contactez Nous' : 'Contact Us'}
                </Button>
              </Link>
            </div>

            {/* Mobile hamburger */}
            <button 
              className="md:hidden p-2" 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile menu dropdown */}
          {mobileMenuOpen && (
            <div className="md:hidden pb-4 space-y-2 border-t border-border/50 pt-3">
              <div className="flex gap-2 mb-2">
                <Button variant="ghost" size="sm" onClick={() => { setLanguage('en'); localStorage.setItem('language', 'en'); }}>EN</Button>
                <Button variant="ghost" size="sm" onClick={() => { setLanguage('fr'); localStorage.setItem('language', 'fr'); }}>FR</Button>
              </div>
              <Link to="/login" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start">{t('login')}</Button>
              </Link>
              <Link to="/register" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button className="btn-primary w-full">{t('register')}</Button>
              </Link>
              <Link to="/contact" className="block" onClick={() => setMobileMenuOpen(false)}>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white w-full">
                  {language === 'fr' ? 'Contactez Nous' : 'Contact Us'}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Hero Carousel with 7 Unique Slides */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <Carousel 
          className="w-full" 
          opts={{ loop: true }}
          plugins={[
            Autoplay({
              delay: 20000,
              stopOnInteraction: true,
            }),
          ]}
        >
          <CarouselContent>
            {/* Slide 1: Hero - Main headline */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1600&q=80"
                  alt="Luxury property"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
                <div className="relative z-10 h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-3xl">
                      <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6">
                        {t('heroTitle')}
                      </h1>
                      <p className="text-xl sm:text-2xl text-white/90 mb-10 italic">
                        {t('heroSub')}
                      </p>
                      
                      {/* 4 Keywords - Security, Trust, Availability, Serenity */}
                      <div className="flex flex-wrap gap-2 sm:gap-4 mb-10">
                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 sm:px-5 py-2 sm:py-3">
                          <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          <span className="text-white font-medium text-sm sm:text-base">{language === 'fr' ? 'Sécurité' : 'Security'}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 sm:px-5 py-2 sm:py-3">
                          <Heart className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          <span className="text-white font-medium text-sm sm:text-base">{language === 'fr' ? 'Confiance' : 'Trust'}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 sm:px-5 py-2 sm:py-3">
                          <Clock className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          <span className="text-white font-medium text-sm sm:text-base">{language === 'fr' ? 'Disponibilité' : 'Availability'}</span>
                        </div>
                        <div className="flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-3 sm:px-5 py-2 sm:py-3">
                          <Sparkles className="h-4 w-4 sm:h-5 sm:w-5 text-white" />
                          <span className="text-white font-medium text-sm sm:text-base">{language === 'fr' ? 'Sérénité' : 'Serenity'}</span>
                        </div>
                      </div>
                      
                      {/* CTAs */}
                      <div className="flex flex-wrap gap-4">
                        <a href="#quote">
                          <Button size="lg" className="bg-white text-primary hover:bg-white/90 font-semibold px-8" data-testid="hero-quote-btn">
                            {t('requestQuote')}
                            <ArrowRight className="ml-2 h-5 w-5" />
                          </Button>
                        </a>
                        <a href="#plans">
                          <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20 font-semibold px-8" data-testid="hero-plans-btn">
                            {t('seePlans')}
                          </Button>
                        </a>
                        <a href="#services">
                          <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20 font-semibold px-8" data-testid="hero-services-btn">
                            {language === 'fr' ? 'Nos Services' : 'Our Services'}
                          </Button>
                        </a>
                        <a href="#about">
                          <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/20 font-semibold px-8" data-testid="hero-about-btn">
                            {language === 'fr' ? 'À Propos' : 'About Us'}
                          </Button>
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 2: Pain Points - What owners worry about */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?auto=format&fit=crop&w=1600&q=80"
                  alt="Property concerns"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-black/30" />
                <div className="relative z-10 h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="max-w-3xl">
                      <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-8">
                        {t('painTitle')}
                      </h2>
                      
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mb-8">
                        {painPoints.map((pain, idx) => (
                          <div key={idx} className="flex items-center gap-4 bg-white/10 backdrop-blur-sm rounded-xl p-4">
                            <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
                              <img src={pain.image} alt={t(pain.key)} className="w-full h-full object-cover" />
                            </div>
                            <span className="text-white font-medium">{t(pain.key)}</span>
                          </div>
                        ))}
                      </div>
                      
                      <div className="flex flex-wrap gap-3">
                        <div className="inline-flex items-center gap-3 bg-primary rounded-full px-6 py-3">
                          <Shield className="h-5 w-5 text-white" />
                          <span className="text-white font-semibold text-base">{t('painSub')}</span>
                        </div>
                        <div className="inline-flex items-center gap-3 bg-primary rounded-full px-6 py-3">
                          <Shield className="h-5 w-5 text-white" />
                          <span className="text-white font-semibold text-base">
                            {language === 'fr'
                              ? "Une maintenance régulière réduit les coûts d'entretien de votre propriété à l'année."
                              : "Regular maintenance reduces the annual upkeep costs of your property."}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 3: 4 Pillars - Services */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1600&q=80"
                  alt="Our services"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
                <div className="relative z-10 h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white text-center mb-10">
                      {t('pillarsTitle')}
                    </h2>
                    
                    <div className="grid md:grid-cols-4 gap-4">
                      {pillars.map((pillar, idx) => (
                        <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-xl p-5 hover:bg-white/20 transition-colors">
                          <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${pillar.gradient} flex items-center justify-center mb-4`}>
                            <pillar.icon className="h-6 w-6 text-white" />
                          </div>
                          <h3 className="font-heading font-semibold text-white text-lg mb-3">
                            {t(pillar.titleKey)}
                          </h3>
                          <ul className="space-y-2">
                            {t(pillar.bulletsKey).slice(0, 3).map((bullet, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                                <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                                <span>{bullet}</span>
                              </li>
                            ))}
                          </ul>
                          <Link to={`/contact?service=${pillar.contactService}`}>
                            <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80 mt-3">
                              {t('learnMore')} <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide: Nous Couvrons - Coverage Map */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1502602898657-3e91760cbb34?auto=format&fit=crop&w=1600&q=80"
                  alt="France"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/60 to-black/70" />
                <div className="relative z-10 h-full flex items-center">
                  <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white text-center mb-2">
                      {language === 'fr' ? 'Nous Couvrons' : 'Our Coverage'}
                    </h2>
                    <p className="text-white/90 text-center mb-8 text-xl font-medium">
                      Nouvelle-Aquitaine, Occitanie, Pays de la Loire
                    </p>
                    <div className="flex flex-col items-center">
                      <div className="max-w-lg w-full">
                        <img 
                          src="/france-regions.png"
                          alt={language === 'fr' ? 'Carte de couverture France' : 'France coverage map'}
                          className="w-full h-auto rounded-2xl shadow-2xl"
                        />
                      </div>
                      <p className="text-white text-center mt-4 sm:mt-6 text-sm sm:text-base max-w-4xl italic font-medium">
                        {language === 'fr'
                          ? 'En 2027 nous couvrirons aussi Provence-Alpes-Côte d\'Azur et Auvergne-Rhône-Alpes'
                          : 'In 2027 we will also cover Provence-Alpes-Côte d\'Azur and Auvergne-Rhône-Alpes'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 4: Plans Teaser */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1600566753086-00f18fb6b3ea?auto=format&fit=crop&w=1600&q=80"
                  alt="Pricing plans"
                  className="absolute inset-0 w-full h-full object-cover brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40" />
                <div className="relative z-10 h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    {/* Title with icon */}
                    <div className="flex justify-center items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                        <Star className="h-7 w-7 text-white" />
                      </div>
                      <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white">
                        {t('plansTitle')}
                      </h2>
                    </div>
                    <p className="text-white/90 text-center mb-4 text-lg">{t('plansSub')}</p>
                    
                    {/* Hourly price badge */}
                    <div className="flex justify-center mb-8">
                      <div className="bg-primary backdrop-blur-sm rounded-full px-6 py-2">
                        <span className="text-white font-semibold">{t('hourlyPrice')}</span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
                      {plans.map((plan, idx) => (
                        <div 
                          key={idx} 
                          className={`relative rounded-2xl p-6 ${plan.popular ? 'bg-white scale-105' : 'bg-white/10 backdrop-blur-sm border border-white/20'}`}
                        >
                          {plan.popular && (
                            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-4 py-1 rounded-full">
                              {language === 'fr' ? 'POPULAIRE' : 'POPULAR'}
                            </div>
                          )}
                          <h3 className={`font-heading font-semibold text-xl mb-2 ${plan.popular ? 'text-foreground' : 'text-white'}`}>
                            {t(plan.name)}
                          </h3>
                          <div className="mb-4">
                            <span className={`text-sm ${plan.popular ? 'text-muted-foreground' : 'text-slate-400'}`}>{t('startingFrom')}</span>
                            <div className="flex items-baseline gap-1">
                              <span className={`text-4xl font-bold ${plan.popular ? 'text-foreground' : 'text-white'}`}>{plan.price}€</span>
                              <span className={`text-sm ${plan.popular ? 'text-muted-foreground' : 'text-slate-400'}`}>{t('perMonth')}</span>
                            </div>
                          </div>
                          <Button 
                            className={`w-full ${plan.popular ? 'btn-primary' : 'bg-white/20 hover:bg-white/30 text-white'}`}
                            onClick={() => setSelectedPlanIdx(selectedPlanIdx === idx ? null : idx)}
                          >
                            {language === 'fr' ? 'Inclus' : 'Included'}
                            <ChevronRight className={`ml-1 h-4 w-4 transition-transform ${selectedPlanIdx === idx ? 'rotate-90' : ''}`} />
                          </Button>
                          {selectedPlanIdx === idx && (
                            <div className="mt-3 space-y-2">
                              {plan.included.map((item, i) => (
                                <div key={i} className={`flex items-center gap-2 p-2 rounded-lg ${plan.popular ? 'bg-primary/5' : 'bg-white/10'}`}>
                                  <Check className={`h-4 w-4 flex-shrink-0 ${plan.popular ? 'text-primary' : 'text-green-400'}`} />
                                  <span className={`text-sm font-medium ${plan.popular ? 'text-foreground' : 'text-white'}`}>
                                    {language === 'fr' ? item.fr : item.en}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    
                    <div className="text-center mt-8">
                      <p className="text-white/80 italic mb-4 text-lg">
                        {language === 'fr' 
                          ? "Économisez en planifiant, le coût d'entretien d'une propriété est toujours plus élevé s'il n'est pas planifié"
                          : "Save with planning, the cost of maintaining a property is always higher if not planned"}
                      </p>
                      <Link to="/register">
                        <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                          {t('viewAllPlans')} <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 5: How it Works */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1635343826162-5842629b68fc?auto=format&fit=crop&w=1600&q=80"
                  alt="How it works"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/60" />
                <div className="relative z-10 h-full flex flex-col justify-between py-16">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    {/* Title with icon */}
                    <div className="flex justify-center items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                        <ClipboardCheck className="h-7 w-7 text-white" />
                      </div>
                      <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white">
                        {t('howTitle')}
                      </h2>
                    </div>
                    <p className="text-white/90 text-center mb-12 text-lg">{t('howSub')}</p>
                    </div>
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-4">
                      {steps.map((step, idx) => (
                        <div key={idx} className="text-center bg-white/10 backdrop-blur-sm rounded-xl p-4">
                          <div className="relative mx-auto mb-4">
                            <div className="w-20 h-20 mx-auto rounded-full overflow-hidden shadow-lg border-3 border-white/30">
                              <img src={step.image} alt={t(step.titleKey)} className="w-full h-full object-cover" />
                            </div>
                          </div>
                          <h3 className="font-heading font-semibold text-white mb-2">{t(step.titleKey)}</h3>
                          <p className="text-sm text-white/90">{t(step.descKey)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 6: Trust & Standards */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1600210492486-724fe5c67fb0?auto=format&fit=crop&w=1600&q=80"
                  alt="Our standards"
                  className="absolute inset-0 w-full h-full object-cover brightness-110"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40" />
                <div className="relative z-10 h-full flex items-center">
                  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                    {/* Title with icon */}
                    <div className="flex justify-center items-center gap-4 mb-4">
                      <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center">
                        <Shield className="h-7 w-7 text-white" />
                      </div>
                      <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white">
                        {t('trustTitle')}
                      </h2>
                    </div>
                    <p className="text-white/90 text-center mb-12 text-lg">{t('trustSub')}</p>
                    
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-6 max-w-5xl mx-auto">
                      {trustItems.map((item, idx) => (
                        <div key={idx} className="bg-white/90 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white transition-colors shadow-lg">
                          <div className="w-14 h-14 mx-auto rounded-full bg-primary flex items-center justify-center mb-4">
                            <item.icon className="h-7 w-7 text-white" />
                          </div>
                          <p className="text-foreground font-medium text-sm">{t(item.key)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </CarouselItem>

            {/* Slide 7: Final CTA - Quote Form */}
            <CarouselItem>
              <div className="relative h-[70vh] sm:h-[85vh] min-h-[500px] sm:min-h-[700px]">
                <img
                  src="https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?auto=format&fit=crop&w=1600&q=80"
                  alt="Contact us"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/40" />
                <div className="relative z-10 h-full flex items-center justify-center">
                  <div className="max-w-2xl mx-auto px-4 text-center">
                    <h2 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-4">
                      {t('ctaTitle')}
                    </h2>
                    <p className="text-white/90 mb-8 text-lg">{t('ctaSub')}</p>
                    <Button
                      size="lg"
                      className="btn-primary text-lg px-10 py-6"
                      onClick={() => document.getElementById('quote')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                      {t('submitQuote')} <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </div>
                </div>
              </div>
            </CarouselItem>
          </CarouselContent>
          <CarouselPrevious className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 border-0 text-white h-14 w-14 z-20" />
          <CarouselNext className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 border-0 text-white h-14 w-14 z-20" />
          
          {/* Slide indicators */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-3 z-20">
            {[1,2,3,4,5,6,7].map((_, idx) => (
              <div key={idx} className="w-3 h-3 rounded-full bg-white/50 hover:bg-white cursor-pointer" />
            ))}
          </div>
        </Carousel>
      </section>

      {/* 2. Pain Points Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="pain-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4 whitespace-nowrap">
              {t('painTitle')}
            </h2>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mb-10">
            {painPoints.map((pain, idx) => (
              <div key={idx} className="text-center">
                <div className="w-16 h-16 mx-auto rounded-full overflow-hidden mb-3">
                  <img src={pain.image} alt={t(pain.key)} className="w-full h-full object-cover" />
                </div>
                <p className="text-sm font-medium text-foreground">{t(pain.key)}</p>
              </div>
            ))}
          </div>
          
          <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 sm:px-6 py-2 sm:py-3">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span className="font-medium text-primary text-sm sm:text-base">{t('painSub')}</span>
            </div>
            <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 sm:px-6 py-2 sm:py-3">
              <Shield className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              <span className="font-medium text-primary text-sm sm:text-base">
                {language === 'fr' 
                  ? "Une maintenance régulière réduit les coûts d'entretien de votre propriété à l'année."
                  : "Regular maintenance reduces the annual upkeep costs of your property."}
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Four Pillars Section */}
      <section className="py-16 md:py-24 bg-white" id="services" data-testid="pillars-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('pillarsTitle')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('pillarsSub')}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pillars.map((pillar, idx) => (
              <Card key={idx} className="card-base hover:shadow-lg transition-shadow" data-testid={`pillar-card-${idx}`}>
                <CardContent className="p-6">
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${pillar.gradient} flex items-center justify-center mb-4`}>
                    <pillar.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg text-foreground mb-4">
                    {t(pillar.titleKey)}
                  </h3>
                  <ul className="space-y-2 mb-4">
                    {t(pillar.bulletsKey).map((bullet, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Check className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <Link to={`/contact?service=${pillar.contactService}`}>
                    <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80">
                      {t('learnMore')} <ChevronRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Plans Teaser Section */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-900 to-slate-800" id="plans" data-testid="plans-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              {t('plansTitle')}
            </h2>
            <p className="text-slate-300 max-w-2xl mx-auto mb-6">
              {t('plansSub')}
            </p>
            {/* Hourly price badge */}
            <div className="inline-flex items-center gap-2 bg-primary rounded-full px-6 py-2">
              <span className="text-white font-semibold">{t('hourlyPrice')}</span>
            </div>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {plans.map((plan, idx) => (
              <div 
                key={idx} 
                className={`relative rounded-2xl p-6 ${plan.popular ? 'bg-white' : 'bg-white/10 backdrop-blur-sm border border-white/20'}`}
                data-testid={`plan-card-${idx}`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-xs font-bold px-3 py-1 rounded-full">
                    {language === 'fr' ? 'POPULAIRE' : 'POPULAR'}
                  </div>
                )}
                <h3 className={`font-heading font-semibold text-xl mb-2 ${plan.popular ? 'text-foreground' : 'text-white'}`}>
                  {t(plan.name)}
                </h3>
                <div className="mb-4">
                  <span className={`text-sm ${plan.popular ? 'text-muted-foreground' : 'text-slate-400'}`}>{t('startingFrom')}</span>
                  <div className="flex items-baseline gap-1">
                    <span className={`text-4xl font-bold ${plan.popular ? 'text-foreground' : 'text-white'}`}>{plan.price}€</span>
                    <span className={`text-sm ${plan.popular ? 'text-muted-foreground' : 'text-slate-400'}`}>{t('perMonth')}</span>
                  </div>
                </div>
                <Button 
                  className={`w-full ${plan.popular ? 'btn-primary' : 'bg-white/20 hover:bg-white/30 text-white'}`}
                  onClick={() => setSelectedPlanIdx(selectedPlanIdx === idx ? null : idx)}
                >
                  {language === 'fr' ? 'Inclus' : 'Included'}
                  <ChevronRight className={`ml-1 h-4 w-4 transition-transform ${selectedPlanIdx === idx ? 'rotate-90' : ''}`} />
                </Button>
                {selectedPlanIdx === idx && (
                  <div className="mt-3 space-y-2" data-testid={`plan-details-${idx}`}>
                    {plan.included.map((item, i) => (
                      <div key={i} className={`flex items-center gap-2 p-2 rounded-lg ${plan.popular ? 'bg-primary/5' : 'bg-white/10'}`}>
                        <Check className={`h-4 w-4 flex-shrink-0 ${plan.popular ? 'text-primary' : 'text-green-400'}`} />
                        <span className={`text-sm font-medium ${plan.popular ? 'text-foreground' : 'text-white'}`}>
                          {language === 'fr' ? item.fr : item.en}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          <div className="text-center mt-8">
            <p className="text-white/80 italic mb-4 text-lg">
              {language === 'fr' 
                ? "Économisez en planifiant, le coût d'entretien d'une propriété est toujours plus élevé s'il n'est pas planifié"
                : "Save with planning, the cost of maintaining a property is always higher if not planned"}
            </p>
            <Link to="/register">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10">
                {t('viewAllPlans')} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 5. How It Works Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="how-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('howTitle')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('howSub')}
            </p>
          </div>
          
          <div className="relative">
            {/* Connection line */}
            <div className="hidden md:block absolute top-12 left-0 right-0 h-0.5 bg-primary/20" />
            
            <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
              {steps.map((step, idx) => (
                <div key={idx} className="relative text-center" data-testid={`step-${idx}`}>
                  <div className="relative z-10 w-24 h-24 mx-auto rounded-full bg-white border-4 border-primary/20 overflow-hidden mb-4 shadow-sm">
                    <img src={step.image} alt={t(step.titleKey)} className="w-full h-full object-cover" />
                    <div className="absolute -top-1 -right-1 w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center text-sm font-bold shadow-md z-10">
                      {idx + 1}
                    </div>
                  </div>
                  <h3 className="font-heading font-semibold text-foreground mb-2">{t(step.titleKey)}</h3>
                  <p className="text-sm text-muted-foreground">{t(step.descKey)}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* 6. Trust & Standards Section */}
      <section className="py-16 md:py-24 bg-slate-50" data-testid="trust-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
              {t('trustTitle')}
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('trustSub')}
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
            {trustItems.map((item, idx) => (
              <div key={idx} className="bg-white rounded-xl p-6 text-center shadow-sm" data-testid={`trust-item-${idx}`}>
                <div className="w-12 h-12 mx-auto rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <item.icon className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium text-foreground">{t(item.key)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Owner (optional - if profile content exists) */}
      {profileContent && (
        <section className="py-16 md:py-24 bg-white" id="about" data-testid="about-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-5 gap-12 items-center">
              <div className="md:col-span-3">
                <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-6">
                  {language === 'fr' ? 'À Propos' : 'About Us'}
                </h2>
                <div className="flex flex-col md:flex-row gap-6 items-start">
                  {profileContent.image_url && (
                    <div className="flex-shrink-0">
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-primary/20 shadow-lg">
                        <img 
                          src={getFullImageUrl(profileContent.image_url)} 
                          alt={profileContent?.name || "Owner"}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  )}
                  <div className="space-y-4 flex-1">
                    <p className="text-lg text-foreground font-semibold">
                      {profileContent?.name || "Philippe Pierrot"}
                    </p>
                    <p className="text-muted-foreground">
                      {language === 'fr' 
                        ? profileContent?.title_fr || "Propriétaire à Pontaillac"
                        : profileContent?.title_en || "Property Owner in Pontaillac"
                      }
                    </p>
                    <p className="text-muted-foreground leading-relaxed">
                      {language === 'fr'
                        ? profileContent?.bio_fr
                        : profileContent?.bio_en
                      }
                    </p>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
                  <img 
                    src={profileContent.cover_image_url ? getFullImageUrl(profileContent.cover_image_url) : "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=600&q=80"}
                    alt={language === 'fr' ? "Notre espace" : "Our space"}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* 7. Final CTA - Quote Form */}
      <section className="py-16 md:py-24 bg-gradient-to-br from-primary to-primary/80" id="quote" data-testid="quote-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
              {t('ctaTitle')}
            </h2>
            <p className="text-white/80">
              {t('ctaSub')}
            </p>
          </div>
          
          <form onSubmit={handleQuoteSubmit} className="bg-white rounded-2xl p-8 shadow-2xl" data-testid="quote-form">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name">{t('yourName')} *</Label>
                <Input
                  id="name"
                  value={quoteForm.name}
                  onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                  required
                  data-testid="quote-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">{t('yourEmail')} *</Label>
                <Input
                  id="email"
                  type="email"
                  value={quoteForm.email}
                  onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                  required
                  data-testid="quote-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">{t('yourPhone')} *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={quoteForm.phone}
                  onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                  required
                  data-testid="quote-phone"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('propertyType')} *</Label>
                <Select value={quoteForm.propertyType} onValueChange={(v) => setQuoteForm({ ...quoteForm, propertyType: v })} required>
                  <SelectTrigger data-testid="quote-property-type">
                    <SelectValue placeholder={t('propertyType')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="house">{t('house')}</SelectItem>
                    <SelectItem value="apartment">{t('apartment')}</SelectItem>
                    <SelectItem value="villa">{t('villa')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>{t('frequency')} *</Label>
                <Select value={quoteForm.frequency} onValueChange={(v) => setQuoteForm({ ...quoteForm, frequency: v })} required>
                  <SelectTrigger data-testid="quote-frequency">
                    <SelectValue placeholder={t('frequency')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="occasional">{t('freqOccasional')}</SelectItem>
                    <SelectItem value="regular">{t('freqRegular')}</SelectItem>
                    <SelectItem value="most">{t('freqMost')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-3 md:col-span-2">
                <Label className="text-base font-semibold">{language === 'fr' ? 'Prestations souhaitées' : 'Desired Services'}</Label>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                  <div className="space-y-2">
                    {prestationsCol1.map(p => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={quoteForm.services.includes(p.id)} onChange={() => toggleService(p.id)} className="rounded border-gray-300 text-primary focus:ring-primary" />
                        {language === 'fr' ? p.fr : p.en}
                      </label>
                    ))}
                  </div>
                  <div className="space-y-2">
                    {prestationsCol2.map(p => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer text-sm">
                        <input type="checkbox" checked={quoteForm.services.includes(p.id)} onChange={() => toggleService(p.id)} className="rounded border-gray-300 text-primary focus:ring-primary" />
                        {language === 'fr' ? p.fr : p.en}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label htmlFor="message">{t('message')} *</Label>
                <Textarea
                  id="message"
                  value={quoteForm.message}
                  onChange={(e) => setQuoteForm({ ...quoteForm, message: e.target.value })}
                  rows={3}
                  required
                  data-testid="quote-message"
                />
              </div>
            </div>
            <Button type="submit" size="lg" className="w-full mt-6 btn-primary" disabled={submitting} data-testid="quote-submit">
              {submitting ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  {language === 'fr' ? 'Envoi...' : 'Sending...'}
                </span>
              ) : (
                <>
                  {t('submitQuote')}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </>
              )}
            </Button>
          </form>
        </div>
      </section>

      {/* Reviews Section */}
      {reviews.length > 0 && (
        <section className="py-16 md:py-24 bg-white" data-testid="reviews-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-4">
                {language === 'fr' ? 'Ce que disent nos clients' : 'What Our Clients Say'}
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {reviews.slice(0, 3).map((review, idx) => (
                <Card key={idx} className="card-base" data-testid={`review-${idx}`}>
                  <CardContent className="p-6">
                    <Quote className="h-8 w-8 text-primary/20 mb-4" />
                    <p className="text-muted-foreground mb-4 italic">"{review.comment}"</p>
                    <div className="flex items-center gap-1">
                      {[1,2,3,4,5].map((star) => (
                        <Star 
                          key={star} 
                          className={`h-4 w-4 ${star <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-muted-foreground'}`} 
                        />
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
            <div className="sm:col-span-2 md:col-span-2">
              <Link to="/" className="flex items-center gap-3 mb-4" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                <img src="/logo.png" alt="3+ Conciergerie" className="h-10 w-10 object-contain" />
                <span className="font-heading font-bold text-xl">Conciergerie</span>
              </Link>
              <p className="text-slate-400 mb-4">
                {language === 'fr'
                  ? "Votre partenaire de confiance pour la gestion et la surveillance de propriété."
                  : "Your trusted partner for property management and monitoring."
                }
              </p>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4 text-red-500">{t('urgentCall')}</h4>
              <a 
                href="tel:0642526827" 
                className="flex items-center gap-2 text-xl font-bold text-red-500 hover:text-red-400"
              >
                <Phone className="h-5 w-5" />
                06 42 52 68 27
              </a>
            </div>
            <div>
              <h4 className="font-heading font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-slate-400">
                <Link to="/contact" className="flex items-center gap-2 hover:text-white">
                  <Mail className="h-4 w-4" />
                  contact@conciergerie.com
                </Link>
                <div className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Pontaillac, France
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-500">
            <p>© 2026 3+ Conciergerie | 3plusconciergerie.com — {t('allRights')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
